/**
 * Chart Color Presets Manager
 * Централизованная система управления цветовыми темами для астрологических карт
 * Following CLAUDE.md - все константы в /src/config/
 */

import { devLog, devWarn } from '../utils/devLog';
import { CLEAN_PRESET } from './presets/cleanColorPreset';
import { BRIGHT_GREEN_PRESET } from './presets/brightGreenColorPreset';

/**
 * Базовая структура цветового пресета
 */
export interface ColorPreset {
  id: string;
  name: string;
  description?: string;
  
  // Цвета элементов карты
  elements: {
    lagna: string;
    hora: string;
    chandra: string;
    planetDefault: string;
  };
  
  // Цвета линий градусов
  degreeLines: {
    mainStroke: string;
    divisionStroke: string;
    majorDivision: string;
    mainWidth: number;
    divisionWidth: number;
    majorDivisionWidth: number;
  };

  // Мухурта чакра деления
  muhurtaDivisions: {
    // Display mode
    renderMode: 'divisions' | 'sectors';  // 'divisions' = lines, 'sectors' = colored sectors

    // Division lines styling (when renderMode = 'divisions')
    centralLineStroke: string;
    centralLineWidth: number;
    majorDivisionStroke: string;
    minorDivisionStroke: string;
    majorDivisionWidth: number;
    minorDivisionWidth: number;
    majorDivisionLength: number;
    minorDivisionLength: number;

    // Sector styling (when renderMode = 'sectors')
    sectorFillColors: string[];   // Array of colors for alternating sectors
    sectorStroke: string;         // Sector border color
    sectorStrokeWidth: number;    // Sector border width
    sectorFillOpacity: number;    // Sector fill opacity (0-1)
    sectorStrokeOpacity: number;  // Sector stroke opacity (0-1)
    minorDivisionOpacity: number; // Minor division lines opacity (0-1)

    // Division intervals (global 360° configuration)
    minorInterval: number;        // Minor division every N degrees (e.g., 6°)
    majorInterval: number;        // Major division every N degrees (e.g., 12°)
    totalSectors: number;         // Total muhurta sectors (30 sectors × 12° = 360°)

    // Sector text styling
    sectorNumberStyle: {
      fontSize: number;
      fontFamily: string;
      fill: string;
      stroke: string;
      strokeWidth: number;
      fontWeight: string;
    };

    rulerCodeStyle: {
      fontSize: number;
      fontFamily: string;
      fill: string;
      stroke: string;
      strokeWidth: number;
      fontWeight: string;
    };
  };


  // Стили меток мухурта чакры
  muhurtaLabels: {
    fontSize: number;
    fontFamily: string;
    fill: string;
    stroke: string;
    strokeWidth: number;
    fontWeight: string;
    // Line from center to label
    lineStroke: string;
    lineWidth: number;
    // Background square
    backgroundFill: string;
    backgroundStroke: string;
    backgroundStrokeWidth: number;
    backgroundSize: number; // Size of the square background
    labelOffset: number; // Distance offset for label positioning
  };

  // Universal label styles for different label types
  labelStyles: {
    default: {
      text: {
        fontSize: number;
        fontFamily: string;
        fontWeight: string;
        fill: string;
        stroke: string;
        strokeWidth: number;
      };
      line: {
        stroke: string;
        strokeWidth: number;
        opacity?: number;
      };
      background: {
        shape: 'square' | 'circle' | 'none';
        size: number;
        fill: string;
        stroke: string;
        strokeWidth: number;
        opacity?: number;
      };
      labelOffset: number;
    };
    planet: {
      text: {
        fontSize: number;
        fontFamily: string;
        fontWeight: string;
        fill: string;
        stroke: string;
        strokeWidth: number;
      };
      line: {
        stroke: string;
        strokeWidth: number;
        opacity?: number;
      };
      background: {
        shape: 'square' | 'circle' | 'none';
        size: number;
        fill: string;
        stroke: string;
        strokeWidth: number;
        opacity?: number;
      };
      labelOffset: number;
    };
    square: {
      text: {
        fontSize: number;
        fontFamily: string;
        fontWeight: string;
        fill: string;
        stroke: string;
        strokeWidth: number;
      };
      line: {
        stroke: string;
        strokeWidth: number;
        opacity?: number;
      };
      background: {
        shape: 'square' | 'circle' | 'none';
        size: number;
        fill: string;
        stroke: string;
        strokeWidth: number;
        opacity?: number;
      };
      labelOffset: number;
    };
    squareOrange: {
      text: {
        fontSize: number;
        fontFamily: string;
        fontWeight: string;
        fill: string;
        stroke: string;
        strokeWidth: number;
      };
      line: {
        stroke: string;
        strokeWidth: number;
        opacity?: number;
      };
      background: {
        shape: 'square' | 'circle' | 'none';
        size: number;
        fill: string;
        stroke: string;
        strokeWidth: number;
        opacity?: number;
      };
      labelOffset: number;
    };
  };

  // Цвета панели деталей
  detailsPanel: {
    background: string;
    sectionBackground: string;
    borderDefault: string;
    borderSection: string;
    borderRow: string;
    planetBorderDefault: string;
    planetBorderHover: string;
    planetBorderActive: string;
    planetFocusOutline: string;
    textPrimary: string;
    textSecondary: string;
    textAccent: string;
    loadingSpinner: string;
    loadingBackground: string;
  };
  
  // Интерактивные состояния
  interactive: {
    hoverBoxShadow: string;
    focusOutlineOffset: string;
    focusVisibleOutlineWidth: string;
    // Стили активных элементов ChartDetails
    planetBorderDefault: string;
    planetBorderHover: string;
    planetBorderActive: string;
    planetFocusOutline: string;
    headerHoverColor: string;
    headerActiveColor: string;
    headerHoverBackground: string;
    sectionHeaderHoverBackground: string;
  };

  // Дополнительные цвета элементов чарта
  chartElements: {
    planetColor: string;
    yogaStroke: string;
    yogaTextFill: string;
    textFill: string;
    yogaColors: string[];
  };

  // House names (Rashi names) styling
  houseNames: {
    fontSize: number;
    fontFamily: string;
    fontWeight: string;
    fill: string;
    opacity: number;
    // Mode-specific settings for symbols mode
    symbolsMode?: {
      fontSize: number;
      fontFamily: string;
      fontWeight: string;
      dy?: number;
    };
  };

  // Planet sprites styling
  planetSprites: {
    radius: number;
    strokeWidth: number;
    fillColor: string;
    connectionLineLength: number;
    // Drag interaction colors
    dragHighlightColor: string;
    dragOtherPlanetsColor: string;
    // Mode-specific text settings
    modes: {
      symbols: { fontSize: number; fontWeight: number; textStroke?: number; fontFamily?: string; dy?: number; };
      short: { fontSize: number; fontWeight: number; textStroke?: number; fontFamily?: string; dy?: number; };
      full: { fontSize: number; fontWeight: number; textStroke?: number; fontFamily?: string; dy?: number; };
    };
  };
}

/**
 * Пресет по умолчанию (чистая базовая схема)
 */
export const DEFAULT_PRESET: ColorPreset = BRIGHT_GREEN_PRESET;

/**
 * Все доступные пресеты
 */
export const COLOR_PRESETS: Record<string, ColorPreset> = {
  [CLEAN_PRESET.id]: CLEAN_PRESET,  // 'default'
  [BRIGHT_GREEN_PRESET.id]: BRIGHT_GREEN_PRESET  // 'bright-green'
};

/**
 * Менеджер цветовых пресетов
 */
export class ChartColorPresetManager {
  private styleElement: HTMLStyleElement | null = null;
  private currentPresetId: string = DEFAULT_PRESET.id; // use DEFAULT_PRESET id
  
  constructor() {
    if (typeof document !== 'undefined') {
      this.createStyleElement();
    }
  }
  
  /**
   * Создает элемент style для динамических CSS переменных
   */
  private createStyleElement(): void {
    this.styleElement = document.createElement('style');
    this.styleElement.id = 'chart-color-preset-variables';
    document.head.appendChild(this.styleElement);
    this.applyDefaultPreset();
  }
  
  /**
   * Генерирует CSS переменные из указанного пресета
   */
  private generateCSSVariables(presetId: string = this.currentPresetId): string {
    const preset = COLOR_PRESETS[presetId] || DEFAULT_PRESET;
    return `
      :root {
        /* Element colors */
        --planet-lagna-color: ${preset.elements.lagna};
        --planet-hora-color: ${preset.elements.hora};
        --planet-chandra-color: ${preset.elements.chandra};
        --planet-default-color: ${preset.elements.planetDefault};
        
        /* Degree line colors */
        --degree-line-main: ${preset.degreeLines.mainStroke};
        --degree-line-division: ${preset.degreeLines.divisionStroke};
        --degree-line-major: ${preset.degreeLines.majorDivision};
        
        /* Details panel colors */
        --chart-details-bg: ${preset.detailsPanel.background};
        --chart-section-bg: ${preset.detailsPanel.sectionBackground};
        --chart-border-default: ${preset.detailsPanel.borderDefault};
        --chart-border-section: ${preset.detailsPanel.borderSection};
        --chart-border-row: ${preset.detailsPanel.borderRow};
        --planet-border-default: ${preset.detailsPanel.planetBorderDefault};
        --planet-border-hover: ${preset.detailsPanel.planetBorderHover};
        --planet-border-active: ${preset.detailsPanel.planetBorderActive};
        --planet-focus-outline: ${preset.detailsPanel.planetFocusOutline};
        --text-primary: ${preset.detailsPanel.textPrimary};
        --text-secondary: ${preset.detailsPanel.textSecondary};
        --text-accent: ${preset.detailsPanel.textAccent};
        --loading-spinner: ${preset.detailsPanel.loadingSpinner};
        --loading-bg: ${preset.detailsPanel.loadingBackground};
        
        /* Interactive states */
        --hover-box-shadow: ${preset.interactive.hoverBoxShadow};
        --focus-outline-offset: ${preset.interactive.focusOutlineOffset};
        --focus-visible-outline-width: ${preset.interactive.focusVisibleOutlineWidth};
        
        /* Planet item interactive states */
        --planet-border-default: ${preset.interactive.planetBorderDefault};
        --planet-border-hover: ${preset.interactive.planetBorderHover};
        --planet-border-active: ${preset.interactive.planetBorderActive};
        --planet-focus-outline: ${preset.interactive.planetFocusOutline};
        
        /* Header interactive states */
        --header-hover-color: ${preset.interactive.headerHoverColor};
        --header-active-color: ${preset.interactive.headerActiveColor};
        --header-hover-bg: ${preset.interactive.headerHoverBackground};
        --section-header-hover-bg: ${preset.interactive.sectionHeaderHoverBackground};
        
        /* Chart elements */
        --chart-planet-color: ${preset.chartElements.planetColor};
        --chart-yoga-stroke: ${preset.chartElements.yogaStroke};
        --chart-yoga-text: ${preset.chartElements.yogaTextFill};
        --chart-text-fill: ${preset.chartElements.textFill};
      }
    `;
  }
  
  /**
   * Применяет указанный пресет
   */
  public applyPreset(presetId: string): void {
    if (COLOR_PRESETS[presetId] && this.styleElement) {
      this.currentPresetId = presetId;
      this.styleElement.textContent = this.generateCSSVariables(presetId);
      
      
      // Force immediate visual update
      setTimeout(() => {
        this.forceVisualUpdate(presetId);
      }, 50);
    }
  }
  
  /**
   * Force visual update of all chart elements
   */
  private forceVisualUpdate(presetId: string): void {
    const preset = COLOR_PRESETS[presetId];
    if (!preset) return;
    
    
    // Update planet sprites
    if (window.d3PlanetSpriteManager) {
      (window.d3PlanetSpriteManager as any).updatePlanetColors();
    }
    
    // Force update all SVG degree lines
    this.updateDegreeLines(preset);
    
    // Force update house backgrounds
    this.updateHouseBackgrounds(preset);
    
    // Force update hora elements
    this.updateHoraElements(preset);
    
    // Force update CSS-styled elements that use variables
    this.updateCSSStyledElements(preset);
    
    // Force update any CSS-styled elements
    this.triggerCSSRefresh();
  }
  
  /**
   * Update all SVG degree lines with new colors
   */
  private updateDegreeLines(preset: ColorPreset): void {
    const degreeLines = document.querySelectorAll('.degree-line, .degree-line path');
    
    degreeLines.forEach((line) => {
      if (line instanceof SVGElement) {
        line.setAttribute('stroke', preset.degreeLines.mainStroke);
      }
    });
    
    // Update connection lines
    const connectionLines = document.querySelectorAll('.planet-connection-line');
    
    connectionLines.forEach((line) => {
      if (line instanceof SVGElement) {
        line.setAttribute('stroke', preset.degreeLines.mainStroke);
      }
    });
  }
  
  /**
   * Update house background rectangles
   */
  private updateHouseBackgrounds(preset: ColorPreset): void {
    const houseBackgrounds = document.querySelectorAll('rect[class*="house"], rect[data-house]');
    
    houseBackgrounds.forEach((rect) => {
      if (rect instanceof SVGElement) {
        rect.setAttribute('fill', preset.detailsPanel.background);
        rect.setAttribute('stroke', preset.detailsPanel.borderDefault);
      }
    });
    
    // Also force recreate background elements if they exist
    if (window.chartRenderer) {
      (window.chartRenderer as any).recreateBackgroundElements();
    }
  }
  
  /**
   * Update hora-specific elements
   */
  private updateHoraElements(preset: ColorPreset): void {
    // The hora planet colors are now handled by D3PlanetSpriteManager.updatePlanetColors()
    // which uses the stored specialType in each sprite
    
    // Just check for any text elements containing "Hora" in labels/UI
    const horaTexts = document.querySelectorAll('text');
    horaTexts.forEach((text) => {
      if (text instanceof SVGElement && text.textContent?.toLowerCase().includes('hora')) {
        text.setAttribute('fill', preset.elements.hora);
      }
    });
    
  }
  /**
   * Update CSS-styled elements that should use preset colors
   */
  private updateCSSStyledElements(preset: ColorPreset): void {
    // Force trigger CSS variable re-evaluation by updating custom properties
    const root = document.documentElement;
    
    // Force update the CSS custom properties
    root.style.setProperty('--chart-details-bg', preset.detailsPanel.background);
    root.style.setProperty('--chart-section-bg', preset.detailsPanel.sectionBackground);  
    root.style.setProperty('--chart-border-default', preset.detailsPanel.borderDefault);
    
    
    // Also update any inline style elements that might have been missed
    const styledElements = document.querySelectorAll('[style*="var(--chart"], .chart-info-panel, .astrology-chart-wrapper');
    
    styledElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        // Force style recalculation by briefly toggling a property
        const originalDisplay = element.style.display;
        element.style.display = 'none';
        void element.offsetHeight; // Force reflow
        element.style.display = originalDisplay;
      }
    });
  }
  
  /**
   * Trigger CSS refresh by briefly toggling a class
   */
  private triggerCSSRefresh(): void {
    const root = document.documentElement;
    root.classList.add('chart-preset-updating');
    setTimeout(() => {
      root.classList.remove('chart-preset-updating');
    }, 10);
  }

  /**
   * Применяет default пресет
   */
  public applyDefaultPreset(): void {
    this.applyPreset(DEFAULT_PRESET.id);
  }
  
  /**
   * Получает текущий активный пресет
   */
  public getCurrentPreset(): ColorPreset {
    const preset = COLOR_PRESETS[this.currentPresetId];
    if (!preset) {
      devWarn('⚠️ Preset not found:', this.currentPresetId, 'falling back to DEFAULT_PRESET');
      return DEFAULT_PRESET;
    }
    return preset;
  }
  
  /**
   * Получает default пресет
   */
  public getDefaultPreset(): ColorPreset {
    return DEFAULT_PRESET;
  }
  
  /**
   * Получает все доступные пресеты
   */
  public getAllPresets(): Record<string, ColorPreset> {
    return COLOR_PRESETS;
  }
  
  /**
   * Получает пресет по ID
   */
  public getPreset(presetId: string): ColorPreset | null {
    return COLOR_PRESETS[presetId] || null;
  }
  
  /**
   * Удаляет style element при очистке
   */
  public cleanup(): void {
    if (this.styleElement) {
      this.styleElement.remove();
      this.styleElement = null;
    }
  }
}

/**
 * Глобальный экземпляр менеджера пресетов
 */
export const chartColorPresetManager = new ChartColorPresetManager();

// Добавляем в window для тестирования в браузерной консоли
if (typeof window !== 'undefined') {
  window.chartColorPresetManager = chartColorPresetManager;
  
  // Простая утилита для тестирования пресетов
  window.presetTest = {
    green() {
      chartColorPresetManager.applyPreset('bright-green');
    },
    
    default() {
      chartColorPresetManager.applyPreset('default');
    },
    
    showColors() {
      const root = document.documentElement;
      devLog('📊 Current CSS variables:');
      devLog('  --degree-line-main:', getComputedStyle(root).getPropertyValue('--degree-line-main'));
      devLog('  --planet-hora-color:', getComputedStyle(root).getPropertyValue('--planet-hora-color'));
      devLog('  --planet-lagna-color:', getComputedStyle(root).getPropertyValue('--planet-lagna-color'));
      devLog('  --planet-default-color:', getComputedStyle(root).getPropertyValue('--planet-default-color'));
    },
    
    current() {
      const preset = chartColorPresetManager.getCurrentPreset();
      devLog('🎨 Current preset:', preset.id, preset.name);
      devLog('  Main stroke:', preset.degreeLines.mainStroke);
      devLog('  Hora color:', preset.elements.hora);
      return preset;
    },
    
    inspectVisualElements() {
      devLog('🔍 Inspecting visual elements:');
      
      // Check degree lines
      const degreeLines = document.querySelectorAll('.degree-line');
      devLog(`  Degree lines: ${degreeLines.length} found`);
      if (degreeLines.length > 0) {
        const firstLine = degreeLines[0] as SVGElement;
        devLog(`  First degree line stroke: ${firstLine.getAttribute('stroke')}`);
      }
      
      // Check planet circles
      const planetCircles = document.querySelectorAll('.planet-circle');
      devLog(`  Planet circles: ${planetCircles.length} found`);
      if (planetCircles.length > 0) {
        const firstCircle = planetCircles[0] as SVGElement;
        devLog(`  First planet circle stroke: ${firstCircle.getAttribute('stroke')}`);
      }
      
      // Check planet text
      const planetTexts = document.querySelectorAll('.planet-text');
      devLog(`  Planet texts: ${planetTexts.length} found`);
      if (planetTexts.length > 0) {
        const firstText = planetTexts[0] as SVGElement;
        devLog(`  First planet text fill: ${firstText.getAttribute('fill')}`);
      }
      
      // Check connection lines
      const connectionLines = document.querySelectorAll('.planet-connection-line');
      devLog(`  Connection lines: ${connectionLines.length} found`);
      if (connectionLines.length > 0) {
        const firstConnection = connectionLines[0] as SVGElement;
        devLog(`  First connection line stroke: ${firstConnection.getAttribute('stroke')}`);
      }
      
      // Check house backgrounds
      const houseBackgrounds = document.querySelectorAll('rect[class*="house"], rect[data-house]');
      devLog(`  House backgrounds: ${houseBackgrounds.length} found`);
      if (houseBackgrounds.length > 0) {
        const firstHouse = houseBackgrounds[0] as SVGElement;
        devLog(`  First house background fill: ${firstHouse.getAttribute('fill')}`);
      }
      
      // Check hora elements
      const horaElements = document.querySelectorAll('[data-type="hora"], .hora');
      devLog(`  Hora elements: ${horaElements.length} found`);
      if (horaElements.length > 0) {
        const firstHora = horaElements[0] as SVGElement;
        devLog(`  First hora element color: ${firstHora.getAttribute('fill') || firstHora.getAttribute('stroke')}`);
      }
      
      // Check styled elements
      const styledElements = document.querySelectorAll('[style*="background"]');
      devLog(`  CSS styled elements: ${styledElements.length} found`);
      if (styledElements.length > 0) {
        const firstStyled = styledElements[0] as HTMLElement;
        devLog(`  First styled element background: ${firstStyled.style.backgroundColor}`);
      }
    }
  };
}

/**
 * Утилита для получения цвета элемента из указанного или текущего пресета
 */
export function getElementColor(
  elementType: 'planet' | 'lagna' | 'hora' | 'chandra', 
  presetId?: string
): string {
  const preset = presetId ? 
    COLOR_PRESETS[presetId] || DEFAULT_PRESET : 
    chartColorPresetManager.getCurrentPreset();
    
  switch (elementType) {
    case 'lagna': return preset.elements.lagna;
    case 'hora': return preset.elements.hora;
    case 'chandra': return preset.elements.chandra;
    default: return preset.elements.planetDefault;
  }
}

// Re-export presets
export { CLEAN_PRESET } from './presets/cleanColorPreset';
export { BRIGHT_GREEN_PRESET } from './presets/brightGreenColorPreset';

export default chartColorPresetManager;